/**
 * Public front-page example-stories strip (Epic 20 S20.2, Task 4).
 *
 * Shows a SMALL teaser set of example stories rendered as large "hero" cards
 * to attract visitors, with a "Selaa kaikkia esimerkkisatuja →" link to the
 * full /originals catalog (all 8). The front page deliberately shows only TWO
 * examples — the newest {@link FEATURED_BANDS youngest + oldest} band stories —
 * so visitors see the age range at a glance and click through to explore more
 * (front-page tweak 2026-06-13). If a featured band has no story yet, the slot
 * falls back to the next populated band so the strip never goes sparse.
 * Loading/empty/error states degrade gracefully so they never block the rest
 * of the page. Hardcoded Finnish (Epic 20 exception).
 *
 * @module components/originals/home/ExampleStories
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AGE_BAND_VALUES } from '@sagamoon/shared';
import { OriginalsStoryCard } from '../OriginalsStoryCard';
import { useOriginalsStories } from '../../../hooks/useOriginalsStories';
import { Skeleton } from '../../ui/Skeleton';

// The front page shows only a couple of examples as a teaser; the full set
// lives on /originals. Two large hero cards (owner decision 2026-06-13).
const FRONT_PAGE_EXAMPLE_LIMIT = 2;

// Feature the youngest + oldest bands so visitors see the age range at a glance
// (owner decision 2026-06-13). Order here = display order (young → old).
const FEATURED_BANDS = ['3-4', '9-10'] as const;

export function ExampleStories(): JSX.Element {
  const { data: stories, isLoading } = useOriginalsStories();

  // Pick the newest story from each featured band (youngest + oldest). If a
  // featured band has no story yet, fall back to the next populated band (band
  // order) so the strip still shows up to FRONT_PAGE_EXAMPLE_LIMIT cards. The
  // hook returns all active stories newest-first, so the first match per band
  // is the newest.
  const examples = useMemo(() => {
    const all = stories ?? [];
    const newestInBand = (band: string) => all.find((s) => s.ageBand === band);

    const picks: NonNullable<ReturnType<typeof newestInBand>>[] = [];
    const used = new Set<string>();
    const add = (band: string): void => {
      if (picks.length >= FRONT_PAGE_EXAMPLE_LIMIT) return;
      const story = newestInBand(band);
      if (story && !used.has(story.slug)) {
        picks.push(story);
        used.add(story.slug);
      }
    };

    FEATURED_BANDS.forEach(add); // preferred: youngest + oldest
    AGE_BAND_VALUES.forEach(add); // fallback: fill any remaining slot

    return picks;
  }, [stories]);

  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-accent-gold">
          Esimerkkejä Sagamoonin saduista
        </p>
        <h2 className="mt-2 font-whimsy text-3xl text-text-twilight-cream">
          Katso, millainen satu lapsellesi voisi syntyä.
        </h2>
        <p className="mt-4 max-w-2xl text-text-twilight-cream/80">
          Nämä esimerkkisadut on tehty samalla työkalulla, jonka sinäkin saat käyttöösi. Selaa eri
          ikäryhmille tehtyjä satuja ja katso sadun lopusta miten ideasta kasvoi ikioma satu.
        </p>

        {isLoading ? (
          <div className="mt-8 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2">
            {Array.from({ length: FRONT_PAGE_EXAMPLE_LIMIT }).map((_, i) => (
              <Skeleton key={i} className="h-96 rounded-2xl" />
            ))}
          </div>
        ) : examples.length > 0 ? (
          <div className="mt-8 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-2">
            {examples.map((story) => (
              <OriginalsStoryCard key={story.slug} story={story} coverHeightClass="h-64 sm:h-72" />
            ))}
          </div>
        ) : null}

        <div className="mt-8">
          <Link
            to="/originals"
            className="inline-flex min-h-11 items-center text-sm font-semibold text-accent-gold hover:opacity-90"
          >
            Selaa kaikkia esimerkkisatuja
            <span aria-hidden="true" className="ml-1">
              →
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
